import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  DndContext, DragOverlay, useDroppable, useDraggable,
  PointerSensor, useSensor, useSensors, pointerWithin,
} from '@dnd-kit/core';
import { keyframes } from '@mui/system';

// ── Constants ─────────────────────────────────────────────────────────────
const COLS = 8;
const WOOD = { light:'#c0402a', dark:'#6a1408', frame:'#2a0606' };
const SP   = ['#818cf8','#34d399','#fb923c','#f472b6','#60a5fa','#a78bfa','#4ade80','#fbbf24'];
const SENT = { positive:'#22c55e', negative:'#E61D50', neutral:'#9ca3af' };

const fmt    = s => { if(!s&&s!==0)return''; const m=Math.floor(s/60),sc=Math.floor(s%60); return `${m}:${String(sc).padStart(2,'0')}`; };
const sentOf = row => SENT[(row?.sentiment||'').toLowerCase()] || SENT.neutral;
const spName = sp => sp?.replace(/SPEAKER[_ ]?/,'') || '?';

// Animations
const dropIn = keyframes`from{transform:scale(1.06) rotate(-2deg)}to{transform:scale(1)}`;
const pulse  = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const glowOn = keyframes`0%,100%{box-shadow:inset 0 0 0 3px #22c55e}50%{box-shadow:inset 0 0 0 4px #22c55e,0 0 20px #22c55e88}`;
const hlBg   = keyframes`0%,100%{box-shadow:0 0 0 2px #f59e0b,0 0 8px rgba(245,158,11,.3)}50%{box-shadow:0 0 0 2px #f59e0b,0 0 16px rgba(245,158,11,.6)}`;

// ── 16:9 thumbnail (contain so vertical clips show fully) ─────────────────
const Thumb = ({ src }) => (
  <Box sx={{ position:'relative', width:'100%', aspectRatio:'16/9',
    background:'#000', overflow:'hidden', flexShrink:0 }}>
    {src && <Box component="img" src={src} sx={{
      position:'absolute', inset:0, width:'100%', height:'100%',
      objectFit:'contain', display:'block' }} />}
  </Box>
);

// ── Speaker chip (clear speaker mark) ─────────────────────────────────────
const SpeakerChip = ({ speaker, color, size=16 }) => (
  <Box sx={{ minWidth:size, height:size, px:'3px', borderRadius:'4px',
    background:color, display:'flex', alignItems:'center', justifyContent:'center',
    boxShadow:'0 1px 3px rgba(0,0,0,.5)' }}>
    <Typography sx={{ fontSize:`${size*0.55}px`, fontWeight:800, color:'#000', lineHeight:1 }}>
      {spName(speaker)}
    </Typography>
  </Box>
);

// ── Source clip card (left panel) — sentiment border, speaker chip ────────
const SourceCard = ({ clip, spColor, seqPos, isHighlighted, onPlay, onDoubleClick }) => {
  const sent = sentOf(clip);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id:`src-${clip.id}`, data:{ clip },
  });
  return (
    <Box ref={setNodeRef} {...listeners} {...attributes}
      style={{ touchAction:'none' }}
      onDoubleClick={()=>onDoubleClick(clip)}
      sx={{
        // Sentiment is the prominent color around the clip
        border:`3px solid ${isHighlighted ? '#f59e0b' : sent}`,
        borderRadius:'8px', overflow:'hidden', background:'#111',
        opacity:isDragging?0.25:1, cursor:'grab', position:'relative',
        boxShadow:isHighlighted ? `0 0 0 2px #f59e0b,0 0 12px rgba(245,158,11,.4)` : `0 2px 8px rgba(0,0,0,.5)`,
        animation:isHighlighted ? `${hlBg} 1.4s ease-in-out infinite` : 'none',
        transition:'border .15s, box-shadow .15s',
        '&:hover':{ boxShadow:`0 0 12px ${sent}77` },
        display:'flex', flexDirection:'column',
      }}>
      <Thumb src={clip.thumbnail} />
      {/* Speaker chip — clear mark, top-left */}
      <Box sx={{ position:'absolute', top:5, left:5, zIndex:5 }}>
        <SpeakerChip speaker={clip.speaker} color={spColor} size={18} />
      </Box>
      {/* Sequence position badge — top-right */}
      {seqPos != null && (
        <Box sx={{ position:'absolute', top:5, right:5, zIndex:5,
          minWidth:18, height:18, px:'4px', borderRadius:'9px', background:'#22c55e',
          display:'flex', alignItems:'center', justifyContent:'center',
          border:'1.5px solid #000' }}>
          <Typography sx={{ fontSize:'9px', fontWeight:800, color:'#000', lineHeight:1 }}>{seqPos}</Typography>
        </Box>
      )}
      <Box sx={{ p:'5px 8px 7px', flex:1 }}>
        <Typography sx={{ fontSize:'8px', color:'rgba(255,255,255,.3)', mb:.3,
          fontVariantNumeric:'tabular-nums' }}>
          {fmt(clip.start_in)} – {fmt(clip.end_in)}
        </Typography>
        <Typography sx={{ fontSize:'10px', color:'rgba(255,255,255,.82)', lineHeight:1.35,
          overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {clip.sentence}
        </Typography>
      </Box>
      {/* Play button */}
      <Box onClick={e=>{ e.stopPropagation(); onPlay(clip); }}
        sx={{ position:'absolute', bottom:4, right:4, zIndex:5,
          width:22, height:22, borderRadius:'50%', cursor:'pointer',
          background:'rgba(0,0,0,.5)', border:`1px solid ${sent}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'10px', color:'#fff',
          '&:hover':{ background:sent, color:'#000' } }}>▶</Box>
    </Box>
  );
};

// ── Empty board square (droppable only) ───────────────────────────────────
const EmptyCell = ({ index, sz, light, isAppend }) => {
  const { setNodeRef, isOver } = useDroppable({ id:`slot-${index}` });
  return (
    <Box ref={setNodeRef} sx={{
      width:sz, height:sz, position:'relative',
      background: isOver ? 'rgba(255,255,255,.28)' : (light?WOOD.light:WOOD.dark),
      border: isOver ? '2px solid rgba(255,255,255,.6)' : '1px solid rgba(0,0,0,.25)',
      boxShadow:'inset 0 1px 0 rgba(255,255,255,.08)',
      transition:'background .1s',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      {isOver && (
        <Box sx={{ width:sz*.3, height:sz*.3, borderRadius:'50%',
          border:'2px dashed rgba(255,255,255,.6)' }} />
      )}
      {isAppend && !isOver && (
        <Typography sx={{ fontSize:sz*.3+'px', color:'rgba(255,255,255,.12)', lineHeight:1 }}>+</Typography>
      )}
    </Box>
  );
};

// ── Filled board square (droppable + draggable for reorder) ───────────────
const FilledCell = ({ index, clip, spColor, sz, playing, highlighted, onClickPiece, onDblPiece, onRemove }) => {
  const { setNodeRef:dropRef, isOver } = useDroppable({ id:`slot-${index}` });
  const { attributes, listeners, setNodeRef:dragRef, isDragging } =
    useDraggable({ id:`seq-${clip.id}`, data:{ clip } });
  const sent = sentOf(clip);

  return (
    <Box ref={dropRef} sx={{
      width:sz, height:sz, position:'relative', overflow:'visible',
      background:'rgba(0,0,0,.4)',
    }}>
      {/* Insert-here indicator (push to the right) */}
      {isOver && (
        <Box sx={{ position:'absolute', left:-3, top:0, bottom:0, width:5, zIndex:20,
          background:'#fff', borderRadius:2, boxShadow:'0 0 10px #fff' }} />
      )}
      <Box ref={dragRef} {...listeners} {...attributes}
        onClick={()=>onClickPiece(clip)}
        onDoubleClick={()=>onDblPiece(clip)}
        sx={{
          position:'absolute', inset:0, borderRadius:'4px', overflow:'hidden',
          cursor:'grab',
          opacity:isDragging?0.3:1,
          // Sentiment is the prominent border; playing→green, highlighted→gold
          border:`3px solid ${playing?'#22c55e':highlighted?'#f59e0b':sent}`,
          animation: playing ? `${glowOn} 1.5s ease-in-out infinite` : `${dropIn} .2s ease`,
          '&:hover .rrmv':{ opacity:1 },
        }}>
        {/* thumbnail (contain) */}
        <Box sx={{ position:'absolute', inset:0, background:'#000' }}>
          {clip.thumbnail && <Box component="img" src={clip.thumbnail}
            sx={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'contain',display:'block' }} />}
        </Box>
        {/* overlay */}
        <Box sx={{ position:'absolute',inset:0,
          background:'linear-gradient(to top,rgba(0,0,0,.78) 0%,rgba(0,0,0,.1) 55%,transparent 100%)' }} />
        {/* order badge — top-left */}
        <Box sx={{ position:'absolute', top:3, left:3, zIndex:3,
          minWidth:15, height:15, px:'3px', borderRadius:'7px',
          background:playing?'#22c55e':'rgba(0,0,0,.8)',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ fontSize:Math.max(7,sz*.085)+'px', fontWeight:800,
            color:playing?'#000':'#fff', lineHeight:1 }}>{index+1}</Typography>
        </Box>
        {/* speaker chip — clear mark, top-right */}
        {sz>=46 && (
          <Box sx={{ position:'absolute', top:3, right:3, zIndex:3 }}>
            <SpeakerChip speaker={clip.speaker} color={spColor} size={Math.max(13,sz*0.26)} />
          </Box>
        )}
        {/* text */}
        {sz>=46 && (
          <Box sx={{ position:'absolute', bottom:0, left:0, right:0, p:'6px 3px 2px', zIndex:3 }}>
            <Typography sx={{ fontSize:Math.max(7,sz*.07)+'px', color:'rgba(255,255,255,.9)', lineHeight:1.2,
              overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
              {clip.sentence}
            </Typography>
          </Box>
        )}
        {/* remove */}
        <Box className="rrmv" onClick={e=>{ e.stopPropagation(); onRemove(clip.id); }}
          sx={{ position:'absolute', bottom:0, right:0, width:15, height:15,
            borderRadius:'3px 0 3px 0', background:'rgba(0,0,0,.85)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'10px', color:'rgba(255,255,255,.6)', cursor:'pointer',
            opacity:0, transition:'opacity .15s', zIndex:10,
            '&:hover':{ background:'#E61D50', color:'#fff' } }}>×</Box>
      </Box>
    </Box>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────
export default function ChessboardView({ rows, videoFile }) {
  const sensors  = useSensors(useSensor(PointerSensor,{ activationConstraint:{ distance:6 } }));
  const clips    = useMemo(()=>rows.filter(r=>!r.space),[rows]);
  const speakers = useMemo(()=>{
    const u=[...new Set(clips.map(c=>c.speaker).filter(Boolean))];
    return Object.fromEntries(u.map((sp,i)=>[sp,SP[i%SP.length]]));
  },[clips]);
  const spColor = useCallback(sp=>speakers[sp]||'#6b7280',[speakers]);

  // The board is an ordered SEQUENCE of clips (plays L→R, wraps to next row)
  const [sequence,setSequence]     = useState([]);
  const [dragId,setDragId]         = useState(null);
  const [playing,setPlaying]       = useState(null);   // clip id playing in under-board player
  const [highlighted,setHighlight] = useState(null);   // clip id highlighted
  const [status,setStatus]         = useState('');
  const [boardPanelW,setBoardW]    = useState(460);
  const [cellSz,setCellSz]         = useState(48);
  const [srcPlaying,setSrcPlay]    = useState(null);   // clip id playing in off-board player
  const [playerH,setPlayerH]       = useState(200);    // resizable under-board player height
  const draggingPlayer = useRef(false);
  const playerStart    = useRef(null);

  const rafRef      = useRef(null);
  const srcVidRef   = useRef(null);
  const boardVidRef = useRef(null);
  const draggingDivider = useRef(false);
  const divStart    = useRef(null);

  // clip id → sequence position (1-based) for source-card badges
  const clipPos = useMemo(()=>{
    const m={}; sequence.forEach((c,i)=>{ m[c.id]=i+1; }); return m;
  },[sequence]);

  // ── Resizable divider ──────────────────────────────────────────────────
  const onDividerMouseDown = (e)=>{ draggingDivider.current=true; divStart.current={x:e.clientX,w:boardPanelW}; e.preventDefault(); };
  useEffect(()=>{
    const onMove=(e)=>{
      if(!draggingDivider.current)return;
      const dx=divStart.current.x-e.clientX;
      const maxBoard=Math.max(400,window.innerWidth-240);
      setBoardW(Math.max(260,Math.min(maxBoard,divStart.current.w+dx)));
    };
    const onUp=()=>{ draggingDivider.current=false; };
    window.addEventListener('mousemove',onMove); window.addEventListener('mouseup',onUp);
    return ()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  // ── Resizable player height ────────────────────────────────────────────
  const onPlayerHandleDown = (e)=>{ draggingPlayer.current=true; playerStart.current={y:e.clientY,h:playerH}; e.preventDefault(); };
  useEffect(()=>{
    const onMove=(e)=>{
      if(!draggingPlayer.current)return;
      const dy=playerStart.current.y-e.clientY; // drag up = taller
      setPlayerH(Math.max(110,Math.min(520,playerStart.current.h+dy)));
    };
    const onUp=()=>{ draggingPlayer.current=false; };
    window.addEventListener('mousemove',onMove); window.addEventListener('mouseup',onUp);
    return ()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  // ── Drag: insert + push (new clip) OR reorder (existing) ───────────────
  const onDragStart = ({active})=>setDragId(active.id);
  const onDragEnd   = ({active,over})=>{
    setDragId(null);
    if(!over)return;
    const overId=String(over.id);
    if(!overId.startsWith('slot-'))return;
    let dest=parseInt(overId.slice(5),10);
    const clip=active.data.current?.clip;
    if(!clip)return;

    setSequence(prev=>{
      const next=[...prev];
      const existing=next.findIndex(c=>c.id===clip.id);
      if(existing!==-1){
        next.splice(existing,1);             // remove old position
        if(existing<dest)dest-=1;            // adjust if we removed before dest
      }
      dest=Math.max(0,Math.min(dest,next.length));
      next.splice(dest,0,clip);              // insert (pushes the rest right)
      return next;
    });
    setHighlight(clip.id);
  };

  const removeFromSeq = useCallback(id=>{
    setSequence(prev=>prev.filter(c=>c.id!==id));
  },[]);

  // ── Off-board player (source panel) ────────────────────────────────────
  const playSrc = useCallback((clip)=>{
    const vid=srcVidRef.current;
    if(!vid||!videoFile)return;
    if(rafRef.current)cancelAnimationFrame(rafRef.current);
    setSrcPlay(clip.id);
    vid.src=videoFile; vid.currentTime=clip.start_in??0;
    const endIn=clip.end_in??((clip.start_in??0)+30);
    vid.addEventListener('play',()=>{
      const check=()=>{
        if(vid.currentTime>=endIn){ vid.pause(); setSrcPlay(null); }
        else rafRef.current=requestAnimationFrame(check);
      };
      rafRef.current=requestAnimationFrame(check);
    },{once:true});
    vid.play().catch(()=>{});
  },[videoFile]);

  // ── Board piece: click = highlight, double-click = play off-board ──────
  const handleClickPiece = useCallback((clip)=>setHighlight(clip.id),[]);
  const handleDblPiece   = useCallback((clip)=>{ setHighlight(clip.id); playSrc(clip); },[playSrc]);

  // ── Under-board sequence player ────────────────────────────────────────
  const stopAll = useCallback(()=>{
    if(rafRef.current)cancelAnimationFrame(rafRef.current);
    [boardVidRef.current,srcVidRef.current].forEach(v=>{ if(v&&!v.paused)v.pause(); });
    setPlaying(null); setSrcPlay(null); setStatus('');
  },[]);

  const playSequence = useCallback(()=>{
    if(!sequence.length)return;
    const vid=boardVidRef.current;
    if(!vid||!videoFile)return;
    stopAll();
    let i = highlighted ? Math.max(0,sequence.findIndex(c=>c.id===highlighted)) : 0;
    const step=()=>{
      if(i>=sequence.length){ setPlaying(null); setStatus('✓ Done'); setTimeout(()=>setStatus(''),2500); return; }
      const clip=sequence[i++];
      setPlaying(clip.id); setHighlight(clip.id);
      setStatus(`▶ ${i}/${sequence.length} · ${spName(clip.speaker)} — "${clip.sentence?.slice(0,42)}"`);
      vid.src=videoFile; vid.currentTime=clip.start_in??0;
      const endIn=clip.end_in??((clip.start_in??0)+30);
      vid.addEventListener('play',()=>{
        if(rafRef.current)cancelAnimationFrame(rafRef.current);
        const check=()=>{ if(vid.currentTime>=endIn){ step(); } else rafRef.current=requestAnimationFrame(check); };
        rafRef.current=requestAnimationFrame(check);
      },{once:true});
      vid.play().catch(()=>{});
    };
    setTimeout(step,30);
  },[sequence,highlighted,videoFile,stopAll]);

  useEffect(()=>()=>{ if(rafRef.current)cancelAnimationFrame(rafRef.current); },[]);

  // ── Drag overlay clip ──────────────────────────────────────────────────
  const activeDrag = useMemo(()=>{
    if(!dragId)return null;
    const id=dragId.replace(/^(src|seq)-/,'');
    const clip=clips.find(c=>String(c.id)===id) || sequence.find(c=>String(c.id)===id);
    return clip?{ clip, spColor:spColor(clip.speaker) }:null;
  },[dragId,clips,sequence,spColor]);

  // ── Build grid: render `sequence` then empty cells.
  // Grows with the sequence and always keeps a spare row to drop into; the grid
  // area scrolls when it gets taller than the panel.
  const rowCount = Math.max(6, Math.ceil((sequence.length+1)/COLS)+1);
  const totalCells = rowCount*COLS;

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin}
      onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <Box sx={{ display:'flex',height:'100%',overflow:'hidden',background:'#0e0101' }}>

        {/* ── LEFT: source clips ──────────────────────────────────────── */}
        <Box sx={{ flex:1, minWidth:0, height:'100%', overflow:'auto',
          background:'rgba(8,2,2,.97)',
          '&::-webkit-scrollbar':{ width:'5px' },
          '&::-webkit-scrollbar-thumb':{ background:'rgba(255,255,255,.12)',borderRadius:'3px' } }}>
          <Box sx={{ px:2,py:1,position:'sticky',top:0,zIndex:10,
            background:'rgba(8,2,2,.97)',borderBottom:'1px solid rgba(255,255,255,.06)',
            display:'flex',alignItems:'center',justifyContent:'space-between' }}>
            <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'3px',
              textTransform:'uppercase',color:'rgba(255,150,60,.5)' }}>
              Clips — drag to board · double-click to play
            </Typography>
            <Typography sx={{ fontSize:'9px',color:'rgba(255,255,255,.2)' }}>
              {clips.length} clips · {sequence.length} in sequence
            </Typography>
          </Box>

          {/* Off-board player (mounted once, container hidden when idle) */}
          <Box sx={{ p:'8px 12px',borderBottom:'1px solid rgba(255,255,255,.06)',
            display:srcPlaying?'block':'none' }}>
            <video ref={srcVidRef} controls
              style={{ width:'100%',maxHeight:180,background:'#000',borderRadius:'8px',display:'block' }} />
          </Box>

          <Box sx={{ p:2,display:'grid',
            gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',
            gap:'10px',alignContent:'start' }}>
            {clips.map(clip=>(
              <SourceCard key={clip.id} clip={clip} spColor={spColor(clip.speaker)}
                seqPos={clipPos[clip.id] ?? null}
                isHighlighted={highlighted===clip.id}
                onPlay={playSrc} onDoubleClick={handleDblPiece} />
            ))}
          </Box>
        </Box>

        {/* ── DIVIDER ──────────────────────────────────────────────────── */}
        <Box onMouseDown={onDividerMouseDown} sx={{
          width:6, flexShrink:0, cursor:'col-resize',
          background:'rgba(255,255,255,.06)',
          '&:hover':{ background:'rgba(255,150,60,.3)' },
          transition:'background .15s',
          display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Box sx={{ width:2,height:40,borderRadius:1,background:'rgba(255,255,255,.2)' }} />
        </Box>

        {/* ── RIGHT: board panel ───────────────────────────────────────── */}
        <Box sx={{ width:boardPanelW, flexShrink:0, height:'100%',
          display:'flex', flexDirection:'column', background:'rgba(8,1,1,.98)' }}>

          {/* Top bar */}
          <Box sx={{ px:1.5,py:.75,borderBottom:'1px solid rgba(255,150,60,.1)',
            flexShrink:0, display:'flex', alignItems:'center', gap:1.5 }}>
            <Typography sx={{ fontSize:'8px',color:'rgba(255,150,60,.5)',fontWeight:700,
              letterSpacing:'2px',textTransform:'uppercase' }}>
              Play Sequence
            </Typography>
            <Box sx={{ display:'flex',alignItems:'center',gap:'6px',ml:'auto' }}>
              <Typography sx={{ fontSize:'8px',color:'rgba(255,255,255,.3)',whiteSpace:'nowrap' }}>Size</Typography>
              <input type="range" min={38} max={96} value={cellSz}
                onChange={e=>setCellSz(+e.target.value)}
                style={{ width:80, accentColor:'#c84428', cursor:'pointer' }} />
              <Typography sx={{ fontSize:'8px',color:'rgba(255,150,60,.5)',fontVariantNumeric:'tabular-nums',width:22 }}>{cellSz}</Typography>
            </Box>
          </Box>

          {/* Scrollable board */}
          <Box sx={{ flex:1, overflow:'auto', px:1.5, pt:1,
            '&::-webkit-scrollbar':{ width:'4px',height:'4px' },
            '&::-webkit-scrollbar-thumb':{ background:'rgba(255,255,255,.1)',borderRadius:'2px' } }}>
            <Box sx={{ background:WOOD.frame, p:'5px 8px 8px 8px', borderRadius:'7px',
              boxShadow:'0 8px 30px rgba(0,0,0,.7),inset 0 1px 0 rgba(255,160,80,.15)',
              border:'1px solid rgba(180,50,30,.3)', display:'inline-block' }}>
              <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${COLS},${cellSz}px)`, gap:'1px' }}>
                {Array.from({length:totalCells},(_,gi)=>{
                  const r=Math.floor(gi/COLS), c=gi%COLS;
                  const light=(r+c)%2===0;
                  if(gi < sequence.length){
                    const clip=sequence[gi];
                    return <FilledCell key={`f-${clip.id}`} index={gi} clip={clip}
                      spColor={spColor(clip.speaker)} sz={cellSz}
                      playing={playing===clip.id} highlighted={highlighted===clip.id}
                      onClickPiece={handleClickPiece} onDblPiece={handleDblPiece}
                      onRemove={removeFromSeq} />;
                  }
                  return <EmptyCell key={`e-${gi}`} index={gi} sz={cellSz} light={light}
                    isAppend={gi===sequence.length} />;
                })}
              </Box>
            </Box>
          </Box>

          {/* Resize handle for the player */}
          <Box onMouseDown={onPlayerHandleDown} sx={{
            flexShrink:0, height:8, cursor:'row-resize',
            display:'flex', alignItems:'center', justifyContent:'center',
            borderTop:'1px solid rgba(255,255,255,.06)',
            background:'rgba(255,255,255,.03)',
            '&:hover':{ background:'rgba(255,150,60,.25)' }, transition:'background .15s' }}>
            <Box sx={{ width:40, height:3, borderRadius:2, background:'rgba(255,255,255,.25)' }} />
          </Box>

          {/* Under-board player — resizable, always visible */}
          <Box sx={{ px:1.5, pt:.5, flexShrink:0, height:playerH }}>
            <video ref={boardVidRef} controls
              style={{ width:'100%', height:'100%', background:'#000', borderRadius:'8px',
                display:'block', objectFit:'contain' }} />
          </Box>

          {/* Controls */}
          <Box sx={{ px:1.5,pt:1,pb:1.5,display:'flex',flexDirection:'column',gap:'6px',flexShrink:0 }}>
            <Box sx={{ display:'flex',gap:'8px',flexWrap:'wrap' }}>
              {[
                {l:'▶ Play Sequence',bg:'rgba(52,180,80,.15)',bo:'rgba(52,180,80,.4)',c:'#34d399',fn:playSequence,off:sequence.length===0},
                {l:'■ Stop',bg:'rgba(230,29,80,.1)',bo:'rgba(230,29,80,.3)',c:'#E61D50',fn:stopAll},
                {l:'Clear',bg:'rgba(255,255,255,.04)',bo:'rgba(255,255,255,.1)',c:'rgba(255,255,255,.4)',
                  fn:()=>{ stopAll(); setSequence([]); setHighlight(null); }},
              ].map(b=>(
                <Box key={b.l} onClick={b.off?undefined:b.fn} sx={{
                  px:1.8,py:.7,borderRadius:'7px',cursor:b.off?'not-allowed':'pointer',
                  fontSize:'11px',fontWeight:700,background:b.bg,
                  border:`1px solid ${b.bo}`,color:b.off?'rgba(255,255,255,.2)':b.c,
                  opacity:b.off?.4:1,'&:hover':b.off?{}:{ filter:'brightness(1.25)' } }}>{b.l}</Box>
              ))}
            </Box>
            {status && (
              <Box sx={{ display:'flex',alignItems:'center',gap:'5px',
                px:1.5,py:.6,borderRadius:'7px',
                background:'rgba(34,197,94,.08)',border:'1px solid rgba(34,197,94,.25)' }}>
                <Box sx={{ width:5,height:5,borderRadius:'50%',background:'#22c55e',flexShrink:0,animation:`${pulse} .8s infinite` }} />
                <Typography sx={{ fontSize:'10px',color:'#22c55e',fontWeight:600,
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{status}</Typography>
              </Box>
            )}
            <Typography sx={{ fontSize:'9px',color:'rgba(255,150,60,.25)' }}>
              {sequence.length} in sequence{highlighted?' · starts at highlighted clip':''} · drag pieces to reorder
            </Typography>
          </Box>
        </Box>
      </Box>

      <DragOverlay>
        {activeDrag && (
          <Box sx={{ width:160,transform:'rotate(4deg)',
            filter:'drop-shadow(0 10px 20px rgba(0,0,0,.85))',pointerEvents:'none' }}>
            <Box sx={{ border:`2px solid ${activeDrag.spColor}`,borderRadius:'8px',overflow:'hidden',background:'#111' }}>
              <Thumb src={activeDrag.clip.thumbnail} />
              <Box sx={{ p:'4px 7px 6px' }}>
                <Typography sx={{ fontSize:'10px',color:'rgba(255,255,255,.85)',lineHeight:1.3,
                  overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical' }}>
                  {activeDrag.clip.sentence}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
}
